import React, { useState, useRef } from "react";

import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";

// Brevo Form Configuration
const FORM_ACTION_URL =
  "https://69d5cafd.sibforms.com/serve/MUIFAO4prUaOWNci7DltcMpo52Ud17yq-w6ioXwQSWlRCOZ4QCRsq1hk6g9zJe-MB64Na6obl2oXBGdwZY1D4jkCHSVxFK8UnTSJabkA-imv8eh26VgmVuijyB9cjXUlvH4EZ2GnrKD7w8d8lvdkKe7l4sq9zMrz-u-cR4i2QTRzDWdsLyANva224gUDF1q0Bqx-O2OS0GnqSORM";

// Validate in JS only — do not use the HTML `pattern` attribute: browsers compile it
// with the regex `v` (unicode sets) flag and reject many valid patterns at runtime.
const EMAIL_REGEX = /^[-A-Za-z0-9._%+]+@[-A-Za-z0-9.]+\.[A-Za-z]{2,}$/i;

export const AlwaysConnected = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    // Update email if it was trimmed
    if (email !== trimmedEmail) {
      setEmail(trimmedEmail);
    }

    const input = inputRef.current;
    if (!input) return;

    if (!trimmedEmail) {
      input.setCustomValidity("Please enter your email address");
      input.focus();
      formRef.current?.reportValidity();
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      input.setCustomValidity("Please enter a valid email address");
      input.focus();
      formRef.current?.reportValidity();
      return;
    }

    input.setCustomValidity("");

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("EMAIL", trimmedEmail);
      formData.append("email_address_check", "");
      formData.append("locale", "en");

      // Submit to Brevo
      await fetch(FORM_ACTION_URL, {
        method: "POST",
        body: formData,
        mode: "no-cors", // Required for Brevo forms
      });

      // Since we're using no-cors, we can't read the response. Assume success if no error was thrown
      setIsSubscribed(true);
      setEmail("");

      // Reset subscribed state after 5 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 5000);
    } catch (error) {
      console.error("Subscription error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative bg-[#A15A3B] flex flex-col items-center justify-center text-center px-5 sm:px-6 md:px-7 lg:px-9 xl:px-12 pt-7 sm:pt-8 md:pt-9 lg:pt-[2.69rem] pb-10 sm:pb-12 md:pb-14 lg:pb-[3.97rem] overflow-hidden">
      <img
        src="/assets/reinforcement-1.svg"
        alt="Reinforcement"
        className="absolute left-0 bottom-0 w-1/3 sm:w-1/2 max-w-[32.3775rem]"
      />
      <img
        src="/assets/reinforcement-2.svg"
        alt="Reinforcement"
        className="absolute top-0 right-0 sm:right-6 lg:right-[2.12rem] w-1/3 sm:w-1/2 max-w-[21rem]"
      />
      <h2 className="font-mobile text-white text-[1.75rem] sm:text-3xl md:text-[2rem] lg:text-[2.125rem] font-normal leading-8 sm:leading-9 md:leading-10 lg:leading-[2.75rem] mb-1 sm:mb-0">
        VLE Mobile, Always Connected, Always Protected
      </h2>
      <p className="text-white text-sm sm:text-[0.925rem] lg:text-base font-medium leading-6 sm:leading-7 lg:leading-[2.1rem] tracking-[-0.03125rem] mb-3 sm:mb-4 lg:mb-[1.22rem]">
        Stay updated with our latest news and offers!
      </p>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        noValidate
        className="relative w-full max-w-[25.9375rem]"
      >
        <Input
          ref={inputRef}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // Clear custom validity when user types
            if (e.target.validity.customError) {
              e.target.setCustomValidity("");
            }
          }}
          className="bg-white w-full h-14 sm:h-16 md:h-[4.25rem] lg:h-[4.5rem] py-2 sm:py-2.5 lg:py-3 pl-5 sm:pl-[1.375rem] lg:pl-6 pr-[7.5rem] sm:pr-32 lg:pr-36 border-none rounded-[3.8125rem] tex-[#0A0C0C] placeholder:text-[#0A0C0C99] text-sm sm:text-[0.825rem] lg:text-base font-medium leading-normal focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
          placeholder="Enter your email"
          disabled={isSubmitting}
          required
        />
        <Button
          type="submit"
          variant="blackBtn"
          disabled={isSubmitting || isSubscribed}
          className={`absolute top-1/2 -translate-y-1/2 right-2 sm:right-2.5 lg:right-3 w-fit h-10 sm:h-11 lg:h-12 px-5 sm:px-[1.375rem] lg:px-6 text-xs sm:text-[0.825rem] lg:text-sm leading-5 sm:leading-6 lg:leading-[1.7rem] disabled:opacity-100 ${
            isSubscribed ? "disabled:bg-[#00E600]" : ""
          }`}
        >
          {/* disabled:bg-[#85857A] */}
          {isSubscribed ? (
            "Subscribed 🎉"
          ) : isSubmitting ? (
            <div className="flex items-center gap-1 sm:gap-1.5">
              <Icons.Spinner className="w-3 h-3 sm:w-3.5 sm:h-3.5 animate-spin lg:w-4 lg:h-4" />
              <span>Subscribing...</span>
            </div>
          ) : (
            "Subscribe"
          )}
        </Button>
      </form>
    </section>
  );
};
